import React, { useState, useEffect, useCallback } from 'react';
import { realtimeMonitoringService } from '../../../services/realtimeMonitoringService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RealtimeMonitoringDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [timeRange, setTimeRange] = useState('1h');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await realtimeMonitoringService?.getMonitoringStats(timeRange);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching monitoring stats:', error);
    }
  }, [timeRange]);

  const fetchRecentEvents = useCallback(async () => {
    try {
      const events = await realtimeMonitoringService?.getRecentEvents(100);
      setRecentEvents(events);
    } catch (error) {
      console.error('Error fetching recent events:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchRecentEvents()]);
      setLoading(false);
    };

    loadData();
  }, [fetchStats, fetchRecentEvents]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStats();
      fetchRecentEvents();
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchStats, fetchRecentEvents]);

  // Subscribe to real-time events
  useEffect(() => {
    const unsubscribe = realtimeMonitoringService?.subscribe((event) => {
      setLiveEvents(prev => [{
        ...event,
        timestamp: new Date()?.toISOString(),
        id: `${Date.now()}-${Math.random()}`
      }, ...prev]?.slice(0, 50));

      // Refresh stats when new events come in
      if (event?.type === 'alert' || event?.type === 'database_write' || event?.type === 'display_failure') {
        fetchStats();
      }
    });

    return unsubscribe;
  }, [fetchStats]);

  const getEventIcon = (event) => {
    if (event?.eventType === 'error' || event?.type === 'display_failure') return 'AlertCircle';
    if (event?.type === 'database_write') {
      return event?.success ? 'CheckCircle' : 'XCircle';
    }
    if (event?.type === 'alert') return 'Bell';
    if (event?.activity_type?.includes('success')) return 'CheckCircle';
    if (event?.activity_type?.includes('failed')) return 'XCircle';
    return 'Activity';
  };

  const getEventColor = (event) => {
    if (event?.eventType === 'error' || event?.type === 'display_failure') return 'text-red-500';
    if (event?.type === 'database_write') {
      return event?.success ? 'text-green-500' : 'text-red-500';
    }
    if (event?.type === 'alert') return 'text-orange-500';
    if (event?.activity_type?.includes('success')) return 'text-green-500';
    if (event?.activity_type?.includes('failed')) return 'text-red-500';
    return 'text-blue-500';
  };

  const getEventTitle = (event) => {
    if (event?.type === 'database_write') {
      return `Database Write ${event?.success ? 'Success' : 'Failed'}`;
    }
    if (event?.type === 'display_failure') {
      return `Display Failure: ${event?.data?.component_name || 'Unknown'}`;
    }
    if (event?.type === 'alert') {
      return `🚨 Alert: ${event?.data?.alert_type || 'System Alert'}`;
    }
    if (event?.activity_type) {
      return event?.activity_type?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
    }
    if (event?.error_type) {
      return `Error: ${event?.error_type}`;
    }
    return 'Unknown Event';
  };

  const getEventDescription = (event) => {
    if (event?.activity_description) return event?.activity_description;
    if (event?.error_message) return event?.error_message;
    if (event?.data?.message) return event?.data?.message;
    if (event?.data?.error_message) return event?.data?.error_message;
    return 'No description available';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date?.toLocaleString();
  };

  const filteredEvents = [...liveEvents, ...recentEvents]?.filter(event => {
    if (filter === 'all') return true;
    if (filter === 'errors') return event?.eventType === 'error' || event?.type === 'display_failure' || event?.activity_type?.includes('failed');
    if (filter === 'success') return event?.type === 'database_write' && event?.success || event?.activity_type?.includes('success');
    if (filter === 'alerts') return event?.type === 'alert';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Real-Time Monitoring Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Live tracking of database operations, errors, and system alerts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
          </select>
          <Button
            onClick={() => setAutoRefresh(!autoRefresh)}
            variant={autoRefresh ? 'default' : 'outline'}
            className="flex items-center gap-2"
          >
            <Icon name={autoRefresh ? 'Pause' : 'Play'} size={16} />
            {autoRefresh ? 'Auto-Refresh On' : 'Auto-Refresh Off'}
          </Button>
        </div>
      </div>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Database Writes */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Database Writes</h3>
              <Icon name="Database" size={24} className="text-blue-500" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="text-2xl font-bold text-foreground">{stats?.databaseWrites?.total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Successful</span>
                <span className="text-lg font-semibold text-green-500">{stats?.databaseWrites?.successful}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Failed</span>
                <span className="text-lg font-semibold text-red-500">{stats?.databaseWrites?.failed}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Success Rate</span>
                <span className="text-lg font-semibold text-foreground">{stats?.databaseWrites?.successRate}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Avg Duration</span>
                <span className="text-sm font-medium text-foreground">{stats?.databaseWrites?.avgDuration}ms</span>
              </div>
            </div>
          </div>

          {/* Display Failures */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Display Failures</h3>
              <Icon name="AlertTriangle" size={24} className="text-orange-500" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="text-2xl font-bold text-foreground">{stats?.displayFailures?.total}</span>
              </div>
              {Object.entries(stats?.displayFailures?.byType)?.map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground truncate">{type}</span>
                  <span className="text-sm font-semibold text-foreground">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Errors */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">System Errors</h3>
              <Icon name="AlertCircle" size={24} className="text-red-500" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="text-2xl font-bold text-foreground">{stats?.errors?.total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Unresolved</span>
                <span className="text-lg font-semibold text-red-500">{stats?.errors?.unresolved}</span>
              </div>
              {Object.entries(stats?.errors?.byType)?.slice(0, 3)?.map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground truncate">{type}</span>
                  <span className="text-sm font-semibold text-foreground">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Live Events */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-foreground">Live Events</h3>
            {autoRefresh && (
              <span className="flex items-center gap-2 text-sm text-green-500">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Live
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-sm rounded-lg ${filter === 'all' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('success')}
              className={`px-3 py-1 text-sm rounded-lg ${filter === 'success' ? 'bg-green-500 text-white' : 'bg-muted text-foreground'}`}
            >
              Success
            </button>
            <button
              onClick={() => setFilter('errors')}
              className={`px-3 py-1 text-sm rounded-lg ${filter === 'errors' ? 'bg-red-500 text-white' : 'bg-muted text-foreground'}`}
            >
              Errors
            </button>
            <button
              onClick={() => setFilter('alerts')}
              className={`px-3 py-1 text-sm rounded-lg ${filter === 'alerts' ? 'bg-orange-500 text-white' : 'bg-muted text-foreground'}`}
            >
              Alerts
            </button>
          </div>
        </div>

        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {filteredEvents?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Activity" size={48} className="mx-auto mb-2 opacity-50" />
              <p>No events to display</p>
            </div>
          ) : (
            filteredEvents?.map((event, index) => (
              <div
                key={event?.id || `${event?.timestamp}-${index}`}
                className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon
                  name={getEventIcon(event)}
                  size={20}
                  className={`mt-0.5 ${getEventColor(event)}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-medium text-foreground text-sm">{getEventTitle(event)}</h4>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(event?.timestamp || event?.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {getEventDescription(event)}
                  </p>
                  {event?.data?.metadata && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {event?.data?.metadata?.playerName && (
                        <span className="mr-3">Player: {event?.data?.metadata?.playerName}</span>
                      )}
                      {event?.data?.metadata?.duration && (
                        <span className="mr-3">Duration: {Math.round(event?.data?.metadata?.duration)}ms</span>
                      )}
                      {event?.data?.metadata?.table_name && (
                        <span>Table: {event?.data?.metadata?.table_name}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RealtimeMonitoringDashboard;